/**
 * Renders mannequin objects in the 3D viewport.
 *
 * Each mannequin is loaded from a GLB file via useGLTF, cloned via
 * SkeletonUtils for independent morph targets, and rendered with the
 * current viewport shading mode. Morph target weights are driven by
 * the mannequinMorphs store state. Bone poses are applied from
 * mannequinBonePoses each frame.
 *
 * When transformMode is "pose" and the mannequin is selected, an
 * interactive bone overlay appears with clickable, screen-space-scaled
 * joint spheres for FK bone rotation.
 */

import { Html, useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import type { ErrorInfo, ReactNode } from "react";
import {
  Component,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as SkeletonUtils from "three/examples/jsm/utils/SkeletonUtils.js";
import {
  add,
  attribute,
  cross,
  dFdx,
  dFdy,
  float,
  mix,
  modelNormalMatrix,
  mul,
  normalize,
  positionView,
  transformNormalToView,
  texture as tslTexture,
  uv,
} from "three/tsl";
import * as THREE from "three/webgpu";
import {
  MeshMatcapNodeMaterial,
  MeshPhysicalNodeMaterial,
  MeshStandardNodeMaterial,
  type NodeMaterial,
} from "three/webgpu";
import { acceleratedRaycast } from "three-mesh-bvh";
import {
  applySceneObjectWorldTransformToObject3D,
  getSceneObjectWorldTransform,
} from "../app/collectionGraph.ts";
import { patchSceneObject } from "../app/sceneObjectMap.ts";
import { useStore } from "../app/store.ts";
import { suppressDirtyTracking } from "../app/storeDirtyTracking.ts";
import { useSelectedLeafObjectIds } from "../app/useSelectedLeafObjectIds.ts";
import { isVisibleInWorkspace } from "../app/visibilityPolicy.ts";
import {
  getRestPose,
  getRestPoses,
  registerBones,
  unregisterBones,
} from "../mannequin/boneRegistry.ts";
import { getAllConstraintOverrides } from "../mannequin/constraints.ts";
import {
  applyCorrectiveInfluences,
  computeCorrectiveWeights,
  discoverCorrectiveBindings,
} from "../mannequin/correctiveShapeKeys.ts";
import {
  consumeLatestResult as consumeIKResult,
  initIKWorker,
} from "../mannequin/ikWorkerClient.ts";
import { useCachedModel } from "../mannequin/modelCache.ts";
import { DEFAULT_MODEL_ID } from "../mannequin/registry.ts";
import { indexMorphsByLayer } from "../mannequin/shapeKeyLayers.ts";
import { applyTwistBones } from "../mannequin/twistBones.ts";
import type { MannequinModel } from "../mannequin/types.ts";
import { identityProvenance } from "../modeling/provenance.ts";
import { buildEdgesAndCornerEdges } from "../modeling/topology.ts";
import type { SceneObject } from "../modeling/types.ts";
import {
  asLoopNormals,
  asRenderTriangleIndices,
  asSharedTriangleIndices,
  DEFAULT_MATERIAL,
  EMPTY_SHARED_TRIANGLE_INDICES,
  getPrimaryMaterialId,
} from "../modeling/types.ts";
import { CubeSpinner } from "../ui/CubeSpinner.tsx";
import { subsurfCore } from "../wasm/subsurf.ts";
import { requestAsyncBoundsTree } from "./asyncBoundsTree.ts";
import { BoneOverlay } from "./BoneOverlay.tsx";
import { computeWeldedNormals } from "./computeWeldedNormals.ts";
import {
  createViewportMaterial,
  sceneMaterialToConfig,
} from "./createViewportMaterial.ts";
import type { GpuNormalCompute } from "./gpuNormalCompute.ts";
import {
  createGpuNormalCompute,
  syncMorphInfluencesFrom,
} from "./gpuNormalCompute.ts";
import {
  installGpuPositionShadowHook,
  patchPositionForGpuSkinning,
} from "./gpuPositionPatch.ts";
import {
  HIDDEN_SELECTION_GHOST_MAT,
  HIDDEN_SELECTION_GHOST_RENDER_ORDER,
  shouldShowHiddenSelectionGhost,
} from "./hiddenSelectionGhost.ts";
import { markInkingCategory } from "./inkingCategories.ts";
import { JointAngleDataPump } from "./JointAngleDebugOverlay.tsx";
import {
  clearMannequinSurfaceSnapshot,
  getMannequinSurfaceSnapshot,
  isMannequinSurfaceDemanded,
  publishMannequinSurfaceSnapshot,
} from "./mannequinSurfaceSnapshots.ts";
import { useMannequinTextures } from "./mannequinTextures.ts";
import {
  getMorphArrays,
  getSkinData,
  getUVs,
  setMorphArrays,
  setSkinData,
} from "./modifiers/helpers.ts";
import type { ModifierGeometry } from "./modifiers/modifierTypes.ts";
import {
  getActiveSelectedObjectIdFromState,
  getSelectionPrepassMaterial,
  SELECTION_OUTLINE_LAYER,
} from "./selectionOutlinePass.ts";
import {
  shadingDiag,
  validateGpuPipeline,
  validateMaterialPatch,
  validatePreDispatch,
} from "./shadingDiag.ts";
import {
  BONE_WEIGHT_VIZ_MAT,
  DIAG_VIZ_MAT,
  SCULPT_DIM_MAT,
  TOPOLOGY_VIZ_MAT,
  WIRE_OVERLAY_MAT,
  WIRE_OVERLAY_XRAY_MAT,
  WIREFRAME_DEPTH_PREPASS_MAT,
} from "./sharedMaterials.ts";
import {
  buildAdjacency,
  computeDensity,
  computeNormalDeviation,
  computeNormalOrientation,
  computeRestEdgeLengths,
  computeStretch,
  densityToColor,
  deviationToColor,
  orientationToColor,
  skinNormals,
  skinPositions,
  stretchToColor,
} from "./topologyViz.ts";
import { useObjectTap } from "./useObjectTap.ts";
import { useSkinnedOverlay } from "./useSkinnedOverlay.ts";
import { useViewportShading } from "./useViewportShading.ts";
import { viewportSupportsSceneInteraction } from "./viewportInteractionPolicy.ts";
// ---------------------------------------------------------------------------
// Proportion controls — driven by shape keys in the GLB model
// ---------------------------------------------------------------------------

/** IDs of all supported proportion regions. */
export const PROPORTION_IDS = [
  "chest",
  "buttocks",
  "waist",
  "biceps",
  "forearms",
  "thighs",
  "calves",
] as const;
export type ProportionId = (typeof PROPORTION_IDS)[number];

/**
 * Blender-style weight color ramp: maps a 0–1 weight to an RGB color.
 * 0.0 = near-black → dark blue → cyan → green → yellow → 1.0 = red.
 */
function weightToColor(w: number, out: Float32Array, offset: number): void {
  w = Math.min(1, Math.max(0, w));
  let r: number, g: number, b: number;
  if (w < 1e-4) {
    r = 0.02;
    g = 0.02;
    b = 0.06;
  } else if (w < 0.15) {
    const t = w / 0.15;
    r = 0.02 * (1 - t);
    g = 0.02 * (1 - t);
    b = 0.06 + t * 0.94;
  } else if (w < 0.35) {
    const t = (w - 0.15) / 0.2;
    r = 0;
    g = t;
    b = 1;
  } else if (w < 0.55) {
    const t = (w - 0.35) / 0.2;
    r = 0;
    g = 1;
    b = 1 - t;
  } else if (w < 0.75) {
    const t = (w - 0.55) / 0.2;
    r = t;
    g = 1;
    b = 0;
  } else {
    const t = (w - 0.75) / 0.25;
    r = 1;
    g = 1 - t;
    b = 0;
  }
  out[offset] = r;
  out[offset + 1] = g;
  out[offset + 2] = b;
}

// ---------------------------------------------------------------------------
// Legacy → NodeMaterial conversion
//
// The WebGPU renderer converts legacy materials to NodeMaterials internally
// via fromMaterial(). However, node properties like normalNode/positionNode
// set on a legacy material must survive that conversion's property copy loop.
// To guarantee correctness, we create the NodeMaterial ourselves so the
// renderer's fromMaterial() sees isNodeMaterial=true and returns it as-is.
// ---------------------------------------------------------------------------

/** Map of legacy material type → NodeMaterial constructor. */
const NODE_MATERIAL_MAP: Record<string, new () => NodeMaterial> = {
  MeshMatcapMaterial: MeshMatcapNodeMaterial,
  MeshPhysicalMaterial: MeshPhysicalNodeMaterial,
  MeshStandardMaterial: MeshStandardNodeMaterial,
};

/**
 * Convert a legacy Three.js material to its NodeMaterial equivalent.
 * Copies all enumerable properties (same as fromMaterial's for..in loop).
 * If the material is already a NodeMaterial, clones it directly.
 */
function toNodeMaterial(material: THREE.Material): THREE.Material {
  if (material.isNodeMaterial) return material.clone();

  const Ctor = NODE_MATERIAL_MAP[material.type];
  if (!Ctor) {
    // Unknown type — fall back to clone and let the renderer handle it
    return material.clone();
  }

  const nodeMat = new Ctor();
  for (const key in material) {
    if (key === "uuid") continue; // preserve unique identity
    try {
      (nodeMat as any)[key] = (material as any)[key];
    } catch {
      // Skip read-only or problematic properties
    }
  }
  return nodeMat;
}

/**
 * Register GPU compute SBAs as geometry attributes so the WebGPU backend
 * creates a single GPU buffer with STORAGE | VERTEX usage flags, allowing
 * both compute writes and vertex shader reads on the same buffer.
 */
function registerComputeBuffersOnGeometry(
  geo: THREE.BufferGeometry,
  gpuNormal: GpuNormalCompute,
): void {
  // Replace the geometry's "normal" attribute with the compute pipeline's
  // StorageBufferAttribute. This makes normalLocal (which reads
  // attribute("normal", "vec3")) automatically get GPU-computed normals.
  // ALL downstream consumers (normalViewGeometry → normalView → matcapUV,
  // environment reflections, etc.) use GPU normals without any normalNode
  // or normalLocal.assign() hack.
  // StorageBufferAttribute gets STORAGE | VERTEX usage flags via
  // Geometries.js's isStorageBufferAttribute check, so the same GPU buffer
  // is written by compute and read by the vertex shader.
  geo.setAttribute("normal", gpuNormal.normalSBA as any);
  // NOTE: positionSBA is NOT registered on the geometry. It's consumed
  // directly via storage() in the positionNode (see applyMannequinNormalNodes).
  // Registering it as a geometry attribute would leak to overlay materials
  // (wireframe, selection, etc.) causing WGSL errors for materials that
  // don't explicitly consume it.
}

// ---------------------------------------------------------------------------
// Mannequin normal patch — reads normals from GPU compute storage buffer
//
// Three.js's morph normal blending diverges from true surface normals when
// multiple targets are active, and bone skinning applies an approximate
// rotation rather than deriving normals from the actual deformed surface.
//
// Fix: a GPU compute pipeline (gpuNormalCompute.ts) replays morph blending +
// bone skinning on the GPU, then computes angle-weighted smooth normals from
// the resulting triangles. The output storage buffer is read directly by the
// vertex shader via normalNode — no CPU round-trip, no debounce.
// ---------------------------------------------------------------------------

/**
 * Patch a material to read normals from the GPU compute pipeline's output
 * storage buffer after the mannequin deformation passes run.
 *
 * When the material has a normalMap, the normalNode uses "perturbation
 * transfer" to compose GPU-computed normals with normal map detail:
 *
 *   1. Build TBN from the original mesh normal + tangent attribute.
 *      This matches the tangent space used during baking (Mikktspace), so
 *      normal map decoding is seam-free.
 *   2. Decode the normal map through this TBN → detail normal in view space.
 *   3. Compute the mesh's base view-space normal (TBN * (0,0,1)).
 *   4. Perturbation = detail normal − mesh base normal.
 *   5. Final normal = normalize(GPU normal + perturbation).
 *
 * This preserves the GPU pipeline's accurate geometric normals (deformation-
 * aware, seam-merged) while applying the normal map's surface micro-detail
 * without tangent-space mismatch at UV island boundaries.
 *
 * The normalScaleUniform is owned by the caller (a stable ref per mannequin
 * instance) and set from the material's normalIntensity. This avoids
 * duck-typing state onto the material and eliminates timing races.
 */
// ---------------------------------------------------------------------------
// DIAGNOSTIC CONTROL — runtime-configurable from browser console:
//
//   window.__SUBD_DIAG.FLAT_NORMAL = true    — then switch render mode
//   window.__SUBD_DIAG.SKIP_NORMAL_NODE = true
//   window.__SUBD_DIAG.SKIP_POSITION_NODE = true
//   window.__SUBD_DIAG.SKIP_NORMAL_MAP = true
//
// After changing a flag, toggle OUT of render mode and back IN so the
// material effect re-runs and rebuilds the shader with the new flag.
// ---------------------------------------------------------------------------

function applyMannequinNormalNodes(
  mat: THREE.Material,
  gpuNormal: GpuNormalCompute,
  _normalScaleUniform: THREE.Vector2,
): void {
  patchPositionForGpuSkinning(mat, gpuNormal);
}

/** Stable zero-vector for originOffset default (avoids new array allocations). */
const ZERO_VEC: [number, number, number] = [0, 0, 0];

// Bones whose names contain these substrings are hidden from the overlay
// (finger bones are too small and too close together for viewport interaction)
const HIDDEN_BONE_PREFIXES = [
  "finger_",
  "arm_twist",
  "forearm_twist",
  "thigh_twist",
  "leg_twist",
];

function isMajorBone(name: string): boolean {
  for (const prefix of HIDDEN_BONE_PREFIXES) {
    if (name.includes(prefix)) return false;
  }
  return true;
}

function compactGpuVec3(
  buffer: ArrayBuffer,
  vertexCount: number,
): Float32Array {
  const src = new Float32Array(buffer);
  const stride = Math.floor(src.length / Math.max(1, vertexCount));
  const out = new Float32Array(vertexCount * 3);
  if (stride < 3) return out;
  for (let i = 0; i < vertexCount; i++) {
    const srcOffset = i * stride;
    const dstOffset = i * 3;
    out[dstOffset] = src[srcOffset];
    out[dstOffset + 1] = src[srcOffset + 1];
    out[dstOffset + 2] = src[srcOffset + 2];
  }
  return out;
}

function buildGeometryIndex(geo: THREE.BufferGeometry): Uint32Array {
  const index = geo.getIndex();
  if (index) return new Uint32Array(index.array as ArrayLike<number>);

  const vertexCount = geo.getAttribute("position")?.count ?? 0;
  const indices = new Uint32Array(vertexCount);
  for (let i = 0; i < vertexCount; i++) indices[i] = i;
  return indices;
}

async function readGpuMannequinSurfaceGeometry(
  renderer: THREE.WebGPURenderer,
  mesh: THREE.SkinnedMesh,
  gpuNormal: GpuNormalCompute,
): Promise<ModifierGeometry | null> {
  if (typeof renderer.getArrayBufferAsync !== "function") return null;
  const positionAttr = mesh.geometry.getAttribute("position");
  if (!positionAttr) return null;

  const vertexCount = positionAttr.count;
  const [positionsBuffer, normalsBuffer] = await Promise.all([
    renderer.getArrayBufferAsync(gpuNormal.positionSBA),
    renderer.getArrayBufferAsync(gpuNormal.normalSBA).catch(() => null),
  ]);
  const positions = compactGpuVec3(positionsBuffer, vertexCount);
  const normals = normalsBuffer
    ? compactGpuVec3(normalsBuffer, vertexCount)
    : null;
  const indices = buildGeometryIndex(mesh.geometry);

  return {
    positions,
    indices: asSharedTriangleIndices(indices),
    normals,
    uvs: null,
    morphArrays: [],
    skinIndex: null,
    skinWeight: null,
    sculptDisplacement: null,
    groups: [],
    edges: null,
    edgeFlags: null,
    edgeCreases: null,
    skinRadii: null,
    provenance: identityProvenance(vertexCount),
  };
}

// ---------------------------------------------------------------------------
// Single mannequin instance
// ---------------------------------------------------------------------------

/** Error boundary that catches GLB load failures and retries with the fallback variant. */
class MannequinLoadBoundary extends Component<
  { fallback: ReactNode; onError: () => void; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(_error: Error, _info: ErrorInfo) {
    this.props.onError();
  }
  componentDidUpdate(prev: { children: ReactNode }) {
    if (prev.children !== this.props.children && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

export function MannequinNode({ obj }: { obj: SceneObject }) {
  const modelId = obj.mannequinModelId ?? DEFAULT_MODEL_ID;
  const [failedId, setFailedId] = useState<string | null>(null);
  const effectiveId = failedId === modelId ? DEFAULT_MODEL_ID : modelId;
  const model = useCachedModel(effectiveId);

  const onLoadError = useCallback(() => {
    if (effectiveId !== DEFAULT_MODEL_ID) {
      console.warn(
        `Failed to load variant ${effectiveId}, falling back to ${DEFAULT_MODEL_ID}`,
      );
      setFailedId(modelId);
    }
  }, [effectiveId, modelId]);

  // Clear failure when the user picks a different variant
  useEffect(() => {
    setFailedId(null);
  }, []);

  if (!model) {
    return (
      <Html center>
        <CubeSpinner size={32} />
      </Html>
    );
  }

  return (
    <MannequinLoadBoundary
      fallback={
        <Html center>
          <CubeSpinner size={32} />
        </Html>
      }
      onError={onLoadError}
    >
      <MannequinNodeInner obj={obj} model={model} />
    </MannequinLoadBoundary>
  );
}

function MannequinNodeInner({
  obj,
  model,
}: {
  obj: SceneObject;
  model: MannequinModel;
}) {
  const gltf = useGLTF(model.url);
  const textureSlots = useMannequinTextures(model);

  const enterPoseMode = useStore((s) => s.enterPoseMode);
  const selected = useSelectedLeafObjectIds().has(obj.id);
  const visible = useStore((s) => s.visibility.get(obj.id) ?? true);
  const renderVisible = useStore((s) => s.renderVisibility.get(obj.id) ?? true);
  const isRenderMode = useStore((s) => s.workspaceMode === "render");
  const {
    viewMode,
    matcapPreset,
    shadowsEnabled,
    showWireframe,
    objectXray,
    wireframeXray,
    showTextures,
    textureMapsEnabled,
    debugMaterial,
    workspaceMode,
  } = useViewportShading();
  const showSceneInteraction = viewportSupportsSceneInteraction(workspaceMode);
  const effectiveVisible = isVisibleInWorkspace({
    workspaceMode,
    viewportVisible: visible,
    renderVisible,
  });
  const showHiddenSelectionGhost = shouldShowHiddenSelectionGhost({
    selected,
    effectiveVisible,
    showSceneInteraction,
  });
  const renderVisibleInViewport = effectiveVisible || showHiddenSelectionGhost;
  const debugEnabled = useStore((s) => s.debugEnabled);
  const activeTopologyViz = useStore((s) => s.activeTopologyViz);
  const shadingDebugFlags = useStore((s) => s.shadingDebugFlags);
  const shadingOverride = useStore((s) => s.shadingOverrides.get(obj.id));
  const resolvedMaterial = useStore(
    (s) =>
      s.materials.get(getPrimaryMaterialId(obj))?.properties ??
      DEFAULT_MATERIAL,
  );
  const isPoseToolActive = useStore((s) => s.poseMode?.objectId === obj.id);
  const objectXrayActive = workspaceMode === "object" && objectXray;
  const handBoneSelected = useStore((s) => {
    if (s.poseMode?.objectId !== obj.id) return false;
    for (const b of s.poseMode.selectedBones) {
      if (b.startsWith("hand") || b.startsWith("finger_")) return true;
    }
    return false;
  });
  const activeBoneName = useStore((s) =>
    s.poseMode?.objectId === obj.id ? s.poseMode?.activeBone : null,
  );
  // Snapshot pattern: the initial JSX position/rotation/scale need a
  // worldTransform value, but the per-frame `useFrame` below overwrites
  // the group's transform every frame from the latest store state.
  // Subscribing to `sceneObjects` + `collections` would re-render this
  // mannequin on every unrelated transform change in the scene.
  const worldTransform = useMemo(
    () =>
      getSceneObjectWorldTransform(
        useStore.getState().sceneObjects,
        useStore.getState().collections,
        obj.id,
      ) ?? {
        position: obj.position,
        rotation: obj.rotation,
        scale: obj.scale,
      },
    [obj.id, obj.position, obj.rotation, obj.scale],
  );

  const mannequinGroupRef = useRef<THREE.Group>(null);

  // Per-frame transform sync — reads the latest transform directly from the
  // store, bypassing React's render batching.  This ensures the mannequin mesh
  // is never a frame behind the data layer during live gizmo drags / snapping.
  const objId = obj.id;
  useFrame(() => {
    const g = mannequinGroupRef.current;
    if (!g) return;
    const latestState = useStore.getState();
    applySceneObjectWorldTransformToObject3D(
      latestState.sceneObjects,
      latestState.collections,
      objId,
      g,
    );
  });

  // Clone the scene so each instance has independent morph targets
  const clonedScene = useMemo(() => {
    const scene = SkeletonUtils.clone(gltf.scene);
    // Strip Mannequin_Head before any downstream traversals see it —
    // mannequins are always rendered headless.
    const headsToRemove: THREE.Object3D[] = [];
    scene.traverse((child) => {
      if (child.name === "Mannequin_Head") headsToRemove.push(child);
    });
    for (const h of headsToRemove) h.removeFromParent();
    scene.traverse((child) => {
      if (child instanceof THREE.Mesh && child.geometry) {
        child.raycast = acceleratedRaycast;
        installGpuPositionShadowHook(child);
        const geo = child.geometry;
        // Delete morph normals BEFORE first render. Three.js's morph normal
        // blending diverges from true surface normals when multiple targets
        // are active. We CPU-recompute normals each frame instead.
        if (geo.morphAttributes?.normal) {
          delete geo.morphAttributes.normal;
        }
      }
    });
    return scene;
  }, [gltf.scene]);

  useEffect(() => {
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh && child.geometry) {
        requestAsyncBoundsTree(child.geometry, child.geometry);
      }
    });
  }, [clonedScene]);

  // NOTE: do not dispose clonedScene geometries or materials on teardown.
  // `SkeletonUtils.clone(gltf.scene)` performs a shallow Object3D clone —
  // geometries and materials are shared by reference with drei's useGLTF
  // cache AND with every other mannequin clone of the same GLB. Disposing
  // them per clone would corrupt sibling clones and subsequent remounts.
  // The per-clone additions made above (deleted `morphAttributes.normal`) are
  // garbage-collected when the cloned Object3D graph goes away. Bounds trees
  // are now built asynchronously on the shared geometry itself, so sibling
  // clones intentionally reuse the same acceleration data. The materials we
  // assign via the apply-material effect are owned by
  // `ownedMannequinMaterialsRef` and disposed there.
  const morphMesh = useMemo(() => {
    // Prefer Mannequin_Body (head-separated GLBs); fall back to first mesh with morphs.
    let body: THREE.Mesh | null = null;
    let fallback: THREE.Mesh | null = null;
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh && child.morphTargetDictionary) {
        if (child.name === "Mannequin_Body") body = child;
        else if (!fallback) fallback = child;
      }
    });
    return body ?? fallback;
  }, [clonedScene]) as THREE.Mesh | null;

  // Find the SkinnedMesh for weight visualization.
  // Prefers Mannequin_Body in head-separated GLBs; falls back to the first
  // SkinnedMesh for older single-mesh GLBs.
  // frustumCulled is disabled on every SkinnedMesh: bone transforms move
  // vertices far beyond the bind-pose bounding sphere, causing incorrect culling.
  const skinnedMesh = useMemo(() => {
    let body: THREE.SkinnedMesh | null = null;
    let fallback: THREE.SkinnedMesh | null = null;
    clonedScene.traverse((child) => {
      if (!child.isSkinnedMesh) return;
      const sm = child as THREE.SkinnedMesh;
      sm.frustumCulled = false;
      if (child.name === "Mannequin_Body") body = sm;
      else if (!fallback) fallback = sm;
    });
    return body ?? fallback;
  }, [clonedScene]) as THREE.SkinnedMesh | null;

  // ---------------------------------------------------------------------------
  // Populate SceneObject with bind-pose geometry from the GLB.
  //
  // addMannequin creates the SceneObject with empty geometry arrays. Viewport
  // interaction and mesh-aware tools still need real vertex data, so we
  // reconstruct it once when the SkinnedMesh first becomes available.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!skinnedMesh || obj.vertices.length > 0) return;
    const geo = skinnedMesh.geometry;
    const posAttr = geo.getAttribute("position");
    const nrmAttr = geo.getAttribute("normal");
    const idxAttr = geo.index;
    if (!posAttr || !nrmAttr || !idxAttr) return;

    const vertices = new Float32Array(posAttr.array as Float32Array);
    const normals = new Float32Array(nrmAttr.array as Float32Array);
    const indices = new Uint32Array(idxAttr.array as Uint32Array);

    // Build triangle topology (each 3 indices = one face)
    const faceCount = indices.length / 3;
    const offsets = new Uint32Array(faceCount + 1);
    for (let i = 0; i <= faceCount; i++) offsets[i] = i * 3;

    // Suppress dirty tracking — this is a load-time reconstruction of
    // geometry from the GLB, not a user edit.
    suppressDirtyTracking(true);
    useStore.setState({
      sceneObjects: patchSceneObject(useStore.getState().sceneObjects, obj.id, {
        vertices,
        loopNormals: asLoopNormals(normals),
        indices: asRenderTriangleIndices(indices),
        topology: {
          offsets,
          corners: indices,
          ...buildEdgesAndCornerEdges(offsets, indices),
        },
      }),
    });
    suppressDirtyTracking(false);
  }, [skinnedMesh, obj.id, obj.vertices.length]);

  // ---------------------------------------------------------------------------
  // High-quality mesh — subdivide geometry in render mode only.
  //
  // Swaps the SkinnedMesh geometry to a Loop-subdivided version when active.
  // morphTargetDictionary and morphTargetInfluences live on the mesh object,
  // not the geometry, so all morph writes (body type, proportions, correctives)
  // continue to work — subdivision just provides denser morphAttributes arrays
  // in the same order.
  // ---------------------------------------------------------------------------

  const baseGeoRef = useRef<THREE.BufferGeometry | null>(null);
  // Subdivided morph arrays — stashed here so the GPU pipeline can read
  // them, while Three.js's vertex shader never sees morphAttributes on the
  // geometry (which would trigger massive morph texture compilation).
  const subdivMorphsRef = useRef<THREE.BufferAttribute[] | null>(null);
  // The GPU compute pipeline handles morph blending in its compute shader.
  // Three.js must NOT also apply morphs in its vertex shader — that would
  // double-apply them and trigger massive morph texture allocation.  We
  // stash the real influences and give the mesh an empty array; the GPU
  // pipeline (and corrective shapes) read from the stashed ref.
  const stashedInfluencesRef = useRef<number[] | null>(null);
  const [geoGeneration, setGeoGeneration] = useState(0);

  useEffect(() => {
    if (!skinnedMesh) return;

    if (isRenderMode) {
      // Stash the original geometry for restoration on mode exit
      if (!baseGeoRef.current) {
        baseGeoRef.current = skinnedMesh.geometry;
      }

      const baseGeo = baseGeoRef.current;

      // Subdivide — feeds the SkinnedMesh's BufferGeometry through the
      // polygon-native subsurf kernel, which propagates morph attributes,
      // skin weights, and UVs. The mannequin mesh
      // ships as triangles with no explicit polygon topology, so the
      // kernel's tri-input path refines each triangle into 3 quads —
      // same behaviour as Blender on a triangulated input.
      const basePos = (
        baseGeo.getAttribute("position") as THREE.BufferAttribute
      ).array as Float32Array;
      const baseIndexAttr = baseGeo.getIndex();
      const baseSkin = getSkinData(baseGeo);
      const subdividedResult = subsurfCore(
        {
          positions: basePos,
          indices: baseIndexAttr
            ? asSharedTriangleIndices(baseIndexAttr.array as Uint32Array)
            : EMPTY_SHARED_TRIANGLE_INDICES,
          normals: null,
          uvs: getUVs(baseGeo),
          morphArrays: getMorphArrays(baseGeo),
          skinIndex: baseSkin?.skinIndex ?? null,
          skinWeight: baseSkin?.skinWeight ?? null,
          sculptDisplacement: null,
          groups: baseGeo.groups.map((g) => ({
            start: g.start,
            count: g.count,
            materialIndex: g.materialIndex,
          })),
          morphTargetsRelative: baseGeo.morphTargetsRelative,
          morphNames: (baseGeo.userData as Record<string, unknown>)
            .morphNames as string[] | undefined,
          edges: null,
          edgeFlags: null,
          skinRadii: null,
          provenance: identityProvenance(basePos.length / 3),
        },
        {
          kind: "subsurf",
          name: "Subdivision",
          enabled: true,
          subdivType: 0,
          levels: 1,
          renderLevels: 1,
        },
      );

      const subdivided = new THREE.BufferGeometry();
      subdivided.setAttribute(
        "position",
        new THREE.BufferAttribute(subdividedResult.positions, 3),
      );
      if (subdividedResult.uvs) {
        subdivided.setAttribute(
          "uv",
          new THREE.BufferAttribute(subdividedResult.uvs, 2),
        );
      }
      if (subdividedResult.indices.length > 0) {
        subdivided.setIndex(
          new THREE.BufferAttribute(subdividedResult.indices, 1),
        );
      }
      if (subdividedResult.morphArrays.length > 0) {
        setMorphArrays(subdivided, baseGeo, subdividedResult.morphArrays);
      }
      if (subdividedResult.skinIndex && subdividedResult.skinWeight) {
        setSkinData(
          subdivided,
          subdividedResult.skinIndex,
          subdividedResult.skinWeight,
        );
      }

      // Stash subdivided morph arrays for the GPU pipeline, then strip from
      // geometry so Three.js doesn't compile morph handling into the vertex
      // shader (48 morphs × 54k verts → 40MB morph texture that fails).
      const subdivMorphPos = subdivided.morphAttributes.position as
        | THREE.BufferAttribute[]
        | undefined;
      subdivMorphsRef.current = subdivMorphPos?.slice() ?? null;
      delete subdivided.morphAttributes.position;

      // Expose stashed morph arrays on userData so the export path can
      // reattach them before baking morph influences into vertex positions.
      skinnedMesh.userData.stashedMorphPositions = subdivMorphsRef.current;

      // Compute normals + tangents synchronously before assigning to the mesh.
      computeWeldedNormals(subdivided);
      if (subdivided.hasAttribute("normal") && subdivided.hasAttribute("uv")) {
        try {
          subdivided.computeTangents();
        } catch {
          // Degenerate triangles can cause computeTangents to throw
        }
      }

      // Copy any base geometry attributes that subdivision doesn't propagate.
      const subdivVertCount = subdivided.getAttribute("position").count;
      const baseAttrNames = Object.keys(baseGeo.attributes);
      const subdivAttrNames = new Set(Object.keys(subdivided.attributes));
      for (const name of baseAttrNames) {
        if (subdivAttrNames.has(name)) continue;
        const baseAttr = baseGeo.getAttribute(name) as THREE.BufferAttribute;
        const itemSize = baseAttr.itemSize;
        subdivided.setAttribute(
          name,
          new THREE.BufferAttribute(
            new Float32Array(subdivVertCount * itemSize),
            itemSize,
          ),
        );
      }

      skinnedMesh.geometry = subdivided;

      // Stash influences if not already done. The non-render pipeline normally
      // stashes them, but that path doesn't run when loading directly into
      // render mode or after a React strict mode unmount/remount cycle.
      if (
        skinnedMesh.morphTargetInfluences &&
        skinnedMesh.morphTargetInfluences.length > 0 &&
        !stashedInfluencesRef.current
      ) {
        stashedInfluencesRef.current = skinnedMesh.morphTargetInfluences;
        skinnedMesh.userData.stashedInfluences = stashedInfluencesRef.current;
        skinnedMesh.morphTargetInfluences = [];
      }

      // Prevent buffer-size mismatch during the React effect cascade.
      // The old material's normalNode reads from the previous pipeline's
      // StorageBufferAttribute (sized for base-resolution verts).  With the
      // new subdivided geometry, the shader would read out-of-bounds from that
      // buffer — a silent WebGPU validation failure that makes the mesh
      // disappear.  Clear the node so the mesh renders with the geometry's own
      // normals (from computeVertexNormals above) until the material effect
      // re-patches with the new pipeline's buffers.
      const mat = skinnedMesh.material as NodeMaterial;
      if (mat?.normalNode) {
        mat.normalNode = null;
        mat.needsUpdate = true;
      }

      // Stash the old pipeline — prevents useFrame dispatch from running
      // compute against old (wrong-sized) buffers.
      if (gpuNormalRef.current) {
        if (staleGpuPipelineRef.current) staleGpuPipelineRef.current.dispose();
        staleGpuPipelineRef.current = gpuNormalRef.current;
        gpuNormalRef.current = null;
      }

      // Create the GPU compute pipeline synchronously here — not via a
      // deferred state cascade.  Previously we set geoGeneration to trigger
      // a separate pipeline effect, but React strict mode's unmount/remount
      // cycle created a window where the pipeline effect fired with stale
      // base-resolution geometry.  Creating inline makes the geometry swap
      // and pipeline creation atomic.
      try {
        gpuNormalRef.current = createGpuNormalCompute(
          skinnedMesh,
          true,
          subdivMorphsRef.current ?? undefined,
        );
        registerComputeBuffersOnGeometry(
          skinnedMesh.geometry,
          gpuNormalRef.current,
        );
        gpuDirty.current = true;
        diagFrameCounter.current = 0;
        const gn = gpuNormalRef.current;
        validateGpuPipeline(skinnedMesh, gn.influenceData);
      } catch (err) {
        shadingDiag.warn(
          "COMPUTE",
          "pipeline-create-failed",
          `createGpuNormalCompute() failed in subdivision effect: ${err}`,
          { error: String(err) },
        );
      }

      setGeoGeneration((g) => g + 1);
      setGpuPipelineGeneration((g) => g + 1);
    } else if (baseGeoRef.current) {
      // Restore original geometry — dispose the subdivided one
      const subdivided = skinnedMesh.geometry;
      skinnedMesh.geometry = baseGeoRef.current;
      baseGeoRef.current = null;
      subdivMorphsRef.current = null;
      delete skinnedMesh.userData.stashedMorphPositions;

      // Keep influences stashed — GPU compute owns morph blending in all
      // modes. The non-render pipeline reads from stashedInfluencesRef.

      // Same buffer-mismatch prevention as entering render mode — clear the
      // normalNode that references the subdivided pipeline's buffers.
      const mat = skinnedMesh.material as NodeMaterial;
      if (mat?.normalNode) {
        mat.normalNode = null;
        mat.needsUpdate = true;
      }
      if (gpuNormalRef.current) {
        if (staleGpuPipelineRef.current) staleGpuPipelineRef.current.dispose();
        staleGpuPipelineRef.current = gpuNormalRef.current;
        gpuNormalRef.current = null;
      }

      // Recreate pipeline at base resolution inline (same rationale as
      // the enter branch — avoid deferred cascade race conditions).
      try {
        gpuNormalRef.current = createGpuNormalCompute(skinnedMesh, true);
        registerComputeBuffersOnGeometry(
          skinnedMesh.geometry,
          gpuNormalRef.current,
        );
        gpuDirty.current = true;
        diagFrameCounter.current = 0;
        validateGpuPipeline(skinnedMesh, gpuNormalRef.current.influenceData);
      } catch (err) {
        shadingDiag.warn(
          "COMPUTE",
          "pipeline-create-failed",
          `createGpuNormalCompute() failed on exit: ${err}`,
          { error: String(err) },
        );
      }

      subdivided.dispose();
      setGeoGeneration((g) => g + 1);
      setGpuPipelineGeneration((g) => g + 1);
    }
  }, [skinnedMesh, isRenderMode]);

  // Separate unmount-only cleanup — restores base geometry and influences
  // if the component unmounts.
  useEffect(() => {
    return () => {
      if (baseGeoRef.current && skinnedMesh) {
        skinnedMesh.geometry = baseGeoRef.current;
        baseGeoRef.current = null;
        subdivMorphsRef.current = null;
        delete skinnedMesh.userData.stashedMorphPositions;
      }
      // Restore stashed influences to mesh (stashed in all modes now).
      if (stashedInfluencesRef.current && skinnedMesh) {
        skinnedMesh.morphTargetInfluences = stashedInfluencesRef.current;
        stashedInfluencesRef.current = null;
        delete skinnedMesh.userData.stashedInfluences;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skinnedMesh]);

  // Collect all bones and build name→Bone map for registry.
  // When a hand bone is selected, finger bones are included in the overlay.
  const { allBones, majorBones, boneMap } = useMemo(() => {
    const all: THREE.Bone[] = [];
    const major: THREE.Bone[] = [];
    const map = new Map<string, THREE.Bone>();
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Bone) {
        all.push(child);
        map.set(child.name, child);
        if (handBoneSelected || isMajorBone(child.name)) major.push(child);
      }
    });
    return { allBones: all, majorBones: major, boneMap: map };
  }, [clonedScene, handBoneSelected]);

  // Register bones in the global registry for gizmo/command access
  useEffect(() => {
    if (boneMap.size > 0) registerBones(obj.id, boneMap, model.id);
    return () => {
      unregisterBones(obj.id);
    };
  }, [obj.id, boneMap, model.id]);

  // Initialize IK worker with rest poses and morph dictionary for corrective weights
  useEffect(() => {
    const rp = getRestPoses(obj.id);
    const dict = morphMesh?.morphTargetDictionary ?? undefined;
    if (rp && boneMap.size > 0) {
      initIKWorker(
        rp as Map<string, [number, number, number, number]>,
        getAllConstraintOverrides() as Record<string, any>,
        dict,
      );
    }
  }, [obj.id, boneMap, morphMesh]);

  // Subscribe to morph weights
  const morphWeights = useStore((s) => s.mannequinMorphs.get(obj.id));

  const debugDisabledLayers = useStore((s) => s.debugDisabledShapeKeyLayers);

  // Apply morph target influences
  useEffect(() => {
    if (!morphMesh || !morphWeights) return;
    const dict = morphMesh.morphTargetDictionary;
    // In render mode, morphTargetInfluences is cleared on the mesh to prevent
    // Three.js from creating morph textures for the morphless subdivided geo.
    // Write to the stashed array instead so the GPU pipeline picks them up.
    const influences =
      stashedInfluencesRef.current ?? morphMesh.morphTargetInfluences;
    if (!dict || !influences) return;
    for (const [name, weight] of morphWeights) {
      const posIdx = dict[`${name}_pos`];
      const negIdx = dict[`${name}_neg`];
      if (posIdx !== undefined) influences[posIdx] = Math.max(0, weight);
      if (negIdx !== undefined) influences[negIdx] = Math.max(0, -weight);
      const directIdx = dict[name];
      if (
        directIdx !== undefined &&
        posIdx === undefined &&
        negIdx === undefined
      ) {
        influences[directIdx] = weight;
      }
    }
  }, [morphMesh, morphWeights]);

  // Normal map uses fixed intensity from material — no body_type modulation.
  const normalMapDisabled = useRef(false);
  useEffect(() => {
    if (normalMapDisabled.current) return;
    const baseIntensity = resolvedMaterial?.normalIntensity ?? 1;
    normalScaleUniform.current.set(baseIntensity, baseIntensity);
  }, [resolvedMaterial]);

  // Apply bone poses and corrective shape keys each frame
  const bonePoses = useStore((s) => s.mannequinBonePoses.get(obj.id));

  // Drive proportion shape keys from boneVolumes store
  const boneVolumes = useStore((s) => s.boneVolumes.get(obj.id));
  useEffect(() => {
    if (!morphMesh) return;
    const dict = morphMesh.morphTargetDictionary;
    const influences =
      stashedInfluencesRef.current ?? morphMesh.morphTargetInfluences;
    if (!dict || !influences) return;

    for (const id of PROPORTION_IDS) {
      const scale = boneVolumes?.get(id) ?? 1.0;
      const weight = scale - 1.0; // 0 at neutral, positive = inflate, negative = deflate
      const posIdx = dict[`prop_${id}_pos`];
      const negIdx = dict[`prop_${id}_neg`];
      if (posIdx !== undefined) influences[posIdx] = Math.max(0, weight);
      if (negIdx !== undefined) influences[negIdx] = Math.max(0, -weight);
    }
  }, [morphMesh, boneVolumes]);

  // Discover corrective shape key bindings from the GLB's morph target dictionary.
  // Returns [] when no corr_* targets exist — zero cost path.
  const correctiveBindings = useMemo(() => {
    const dict = morphMesh?.morphTargetDictionary ?? undefined;
    return discoverCorrectiveBindings(dict);
  }, [morphMesh]);

  // Pre-index morph targets by layer for debug disable enforcement.
  const morphLayerIndex = useMemo(
    () => indexMorphsByLayer(morphMesh?.morphTargetDictionary ?? undefined),
    [morphMesh],
  );

  // Priority -1: bone poses must be applied before GPU compute (priority 0)
  // and before the contact shadow pass (also priority 0) reads bone matrices.
  useFrame(() => {
    // Apply bone poses — restore to rest pose for bones without stored poses
    if (allBones.length > 0) {
      for (const bone of allBones) {
        const q = bonePoses?.get(bone.name);
        if (q) {
          bone.quaternion.set(q[0], q[1], q[2], q[3]);
        } else {
          const rq = getRestPose(obj.id, bone.name);
          if (rq) bone.quaternion.set(rq[0], rq[1], rq[2], rq[3]);
        }
      }
    }

    // Drive twist bones from parent bone rotations
    if (allBones.length > 0) {
      applyTwistBones(obj.id, boneMap);
    }

    // Apply corrective shape keys using pose-space deformation (quaternion distance).
    // When the IK worker is active, corrective weights arrive alongside the IK
    // result — consume them here for zero main-thread cost. Fall back to the
    // synchronous path when the worker hasn't provided weights this frame.
    const corrInfluences =
      stashedInfluencesRef.current ?? morphMesh?.morphTargetInfluences;
    if (
      correctiveBindings.length > 0 &&
      corrInfluences &&
      corrInfluences.length > 0
    ) {
      const workerResult = consumeIKResult();
      if (workerResult?.correctiveWeights) {
        // Apply worker-computed corrective weights
        for (const [index, weight] of workerResult.correctiveWeights) {
          corrInfluences[index] = weight;
        }
        if (debugEnabled) {
          const wMap = new Map(workerResult.correctiveWeights);
          window.__poseref_correctiveDebug = {
            bindings: correctiveBindings,
            weights: wMap,
          };
        }
      } else {
        // Sync fallback: compute on main thread
        const rp = getRestPoses(obj.id);
        if (rp) {
          const weights = computeCorrectiveWeights(
            correctiveBindings,
            boneMap,
            rp,
            bonePoses,
          );
          applyCorrectiveInfluences(corrInfluences, weights);
          if (debugEnabled) {
            window.__poseref_correctiveDebug = {
              bindings: correctiveBindings,
              weights,
            };
          }
        }
      }
    }

    // Debug: isolate a single corrective (zero all other corrective morphs)
    const debugInf =
      stashedInfluencesRef.current ?? morphMesh?.morphTargetInfluences;
    if (debugEnabled && debugInf && debugInf.length > 0) {
      const isolateSet = window.__poseref_correctiveIsolate;
      if (isolateSet && isolateSet.size > 0) {
        const corrIndices = morphLayerIndex.get("corrective");
        if (corrIndices) {
          for (const idx of corrIndices) {
            if (!isolateSet.has(idx)) debugInf[idx] = 0;
          }
        }
      }
    }

    // Debug: zero out morph targets for disabled layers (single enforcement point)
    if (
      debugEnabled &&
      isPoseToolActive &&
      debugDisabledLayers.size > 0 &&
      debugInf &&
      debugInf.length > 0
    ) {
      const inf = debugInf;
      for (const layer of debugDisabledLayers) {
        const indices = morphLayerIndex.get(layer);
        if (indices) {
          for (const idx of indices) inf[idx] = 0;
        }
      }
    }
  }, -1);

  // ---------------------------------------------------------------------------
  // GPU normal compute pipeline
  //
  // Three.js r183 has two problems with mannequin normals:
  //   1. Morph normal blending diverges from true surface normals when
  //      multiple targets are active.
  //   2. Bone skinning applies an approximate rotation rather than deriving
  //      normals from the actual deformed surface.
  //
  // Fix: a GPU compute pipeline replays morph blending + bone skinning and
  // computes angle-weighted smooth normals from the resulting triangles.
  // Everything stays GPU-side — no CPU round-trip, no debounce, correct
  // normals every frame.
  // ---------------------------------------------------------------------------

  const gpuNormalRef = useRef<GpuNormalCompute | null>(null);
  // Incremented when GPU pipeline is recreated — forces the material-apply
  // effect to re-run so materials get re-patched with the new normalBuffer.
  const [gpuPipelineGeneration, setGpuPipelineGeneration] = useState(0);

  // Normal map scale uniform — one stable Vector2 per mannequin instance.
  // Set from material normalIntensity. Passed into applyMannequinNormalNodes
  // so the TSL shader reads it without recompiling.
  const normalScaleUniform = useRef(new THREE.Vector2(0, 0));

  // Dirty flag for GPU compute dispatch — skip when no inputs changed.
  const gpuDirty = useRef(true);
  const gpuSurfaceRevision = useRef(0);
  const surfaceComputeInFlight = useRef(false);
  const surfaceReadbackInFlight = useRef(false);
  const lastBonePosesRef = useRef<
    Map<string, [number, number, number, number]> | undefined
  >(undefined);

  // Track the material this mannequin owns. The apply-material effect
  // allocates a fresh body material each run and disposes the previous
  // one on replay / unmount.
  const ownedMannequinMaterialsRef = useRef<{
    body: THREE.Material;
  } | null>(null);

  // Old pipeline pending disposal — kept alive until the material effect
  // re-patches with the new pipeline's buffers. Disposing immediately would
  // zero out SBA arrays that the current material's normalNode still reads,
  // causing WebGPU validation failures and a blank mesh for one frame.
  const staleGpuPipelineRef = useRef<GpuNormalCompute | null>(null);

  const scheduleMannequinSurfaceReadback = useCallback(
    (renderer: THREE.WebGPURenderer, sourceKey: string) => {
      if (!skinnedMesh || !gpuNormalRef.current) return;
      if (!isMannequinSurfaceDemanded(obj.id)) return;
      if (getMannequinSurfaceSnapshot(obj.id)?.sourceKey === sourceKey) return;
      if (surfaceReadbackInFlight.current) return;

      const mesh = skinnedMesh;
      const gpuNormal = gpuNormalRef.current;
      surfaceReadbackInFlight.current = true;
      void readGpuMannequinSurfaceGeometry(renderer, mesh, gpuNormal)
        .then((geometry) => {
          if (gpuNormalRef.current !== gpuNormal) return;
          if (!geometry || !isMannequinSurfaceDemanded(obj.id)) return;
          publishMannequinSurfaceSnapshot(obj.id, sourceKey, geometry);
        })
        .catch((err) => {
          shadingDiag.warn(
            "COMPUTE",
            "surface-readback-failed",
            `Mannequin surface readback failed: ${err}`,
            { error: String(err), objectId: obj.id },
          );
        })
        .finally(() => {
          surfaceReadbackInFlight.current = false;
        });
    },
    [obj.id, skinnedMesh],
  );

  useEffect(() => {
    return () => {
      clearMannequinSurfaceSnapshot(obj.id);
    };
  }, [obj.id]);

  // Create the GPU compute pipeline when the skinned mesh first appears.
  // In render mode, pipeline creation is handled inline by the subdivision
  // effect (above) — geometry swap and pipeline creation must be atomic to
  // avoid React strict mode race conditions with deferred state cascades.
  // This effect only handles the initial mount and non-render-mode geometry.
  useEffect(() => {
    if (!skinnedMesh) {
      if (gpuNormalRef.current) {
        staleGpuPipelineRef.current = gpuNormalRef.current;
        gpuNormalRef.current = null;
      }
      return;
    }

    // In render mode, the subdivision effect creates the pipeline inline.
    // Skip here to avoid recreating at the wrong resolution.
    if (isRenderMode) return;

    // If we already have a pipeline at the correct resolution, skip.
    const geo = skinnedMesh.geometry;
    const vertCount = geo.getAttribute("position")?.count ?? 0;
    if (gpuNormalRef.current) {
      const existingVerts = gpuNormalRef.current.positionSBA?.count ?? 0;
      if (existingVerts === vertCount) return;
    }

    // Stash the old pipeline — the material still references its buffers
    // until the material-apply effect re-patches.
    if (gpuNormalRef.current) {
      staleGpuPipelineRef.current = gpuNormalRef.current;
      gpuNormalRef.current = null;
    }
    gpuDirty.current = true;
    diagFrameCounter.current = 0;

    const idxCount = geo.index?.count ?? 0;
    const hasNormal = geo.hasAttribute("normal");
    const hasUV = geo.hasAttribute("uv");

    shadingDiag.log(
      "COMPUTE",
      "pipeline-init",
      `Creating pipeline — ${vertCount} verts, ${idxCount / 3} tris`,
      {
        geoId: geo.id,
        hasTangent: geo.hasAttribute("tangent"),
        hasNormal,
        hasUV,
        hasSkinIndex: geo.hasAttribute("skinIndex"),
        hasMorphPos: !!geo.morphAttributes?.position,
      },
    );

    if (!geo.hasAttribute("tangent") && hasNormal && hasUV) {
      try {
        geo.computeTangents();
        shadingDiag.log(
          "BUFFER",
          "tangents-computed",
          `Computed tangent attribute: ${geo.getAttribute("tangent").count} verts`,
        );
      } catch (err) {
        shadingDiag.warn(
          "BUFFER",
          "tangents-failed",
          `computeTangents() failed: ${err}`,
          { error: String(err) },
        );
      }
    } else if (!geo.hasAttribute("tangent")) {
      shadingDiag.warn(
        "BUFFER",
        "tangents-missing-prereqs",
        `Cannot compute tangents — missing: ${!hasNormal ? "normal " : ""}${!hasUV ? "uv" : ""}`,
      );
    }

    try {
      gpuNormalRef.current = createGpuNormalCompute(skinnedMesh, true);
      registerComputeBuffersOnGeometry(
        skinnedMesh.geometry,
        gpuNormalRef.current,
      );
    } catch (err) {
      shadingDiag.warn(
        "COMPUTE",
        "pipeline-create-failed",
        `createGpuNormalCompute() failed: ${err}`,
        { error: String(err) },
      );
      return;
    }

    // Stash influences so Three.js doesn't CPU-blend morphs — GPU compute
    // owns all morph blending via its influenceData buffer.
    if (skinnedMesh.morphTargetInfluences && !stashedInfluencesRef.current) {
      stashedInfluencesRef.current = skinnedMesh.morphTargetInfluences;
      skinnedMesh.userData.stashedInfluences = stashedInfluencesRef.current;
      skinnedMesh.morphTargetInfluences = [];
    }

    validateGpuPipeline(skinnedMesh, gpuNormalRef.current.influenceData);
    setGpuPipelineGeneration((g) => g + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skinnedMesh, isRenderMode]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      staleGpuPipelineRef.current?.dispose();
      staleGpuPipelineRef.current = null;
      gpuNormalRef.current?.dispose();
      gpuNormalRef.current = null;
      const owned = ownedMannequinMaterialsRef.current;
      if (owned) {
        owned.body.dispose();
      }
      ownedMannequinMaterialsRef.current = null;
    };
  }, []);

  // Expose current mesh + pipeline for snapshot system
  useEffect(() => {
    if (!skinnedMesh || !gpuNormalRef.current) return;
    const mesh = skinnedMesh;
    const normalSBA = gpuNormalRef.current.normalSBA;
    // Override snapshot to use live references
    const origSnapshot = shadingDiag.snapshot;
    shadingDiag.snapshot = () => origSnapshot(mesh, normalSBA);
    return () => {
      shadingDiag.snapshot = origSnapshot;
    };
  }, [skinnedMesh]);

  // ---------------------------------------------------------------------------
  // Shading debug — additional diagnostic shader patches (debug mode only)
  // ---------------------------------------------------------------------------

  const patchedMaterialRef = useRef<THREE.MeshStandardMaterial | null>(null);
  const unpatchedMaterialRef = useRef<THREE.MeshStandardMaterial | null>(null);

  const needDebugPatch =
    debugEnabled &&
    isPoseToolActive &&
    (shadingDebugFlags.screenSpaceNormals ||
      shadingDebugFlags.blendedNormals ||
      shadingDebugFlags.normalDirectionViz ||
      shadingDebugFlags.normalMapViz);

  const applyDebugPatch = useCallback(
    (baseMat: THREE.MeshStandardMaterial) => {
      const {
        screenSpaceNormals,
        blendedNormals,
        normalDirectionViz,
        normalMapViz,
      } = useStore.getState().shadingDebugFlags;
      const gpuNormal = gpuNormalRef.current;

      // Normal map viz — render the baked normal map with exaggerated contrast
      // so subtle deviations from neutral (0.5, 0.5, 1.0) are clearly visible
      if (normalMapViz && textureSlots?.normal) {
        const vizMat = new THREE.MeshBasicNodeMaterial();
        const raw = tslTexture(textureSlots.normal, uv());
        const gain = float(3.0);
        vizMat.colorNode = add(mul(add(raw, float(-0.5)), gain), float(0.5));
        vizMat.needsUpdate = true;
        return vizMat as unknown as THREE.MeshStandardMaterial;
      }

      // Normal direction viz uses an unlit material to show raw normal RGB
      if (normalDirectionViz && gpuNormal) {
        const vizMat = new THREE.MeshBasicNodeMaterial();
        const objN = attribute("normal", "vec3");
        const worldN = normalize((modelNormalMatrix as any).mul(objN));
        vizMat.colorNode = add(mul(worldN, float(0.5)), float(0.5));
        vizMat.needsUpdate = true;
        return vizMat as unknown as THREE.MeshStandardMaterial;
      }

      const patched = baseMat.clone();

      if (gpuNormal) {
        applyMannequinNormalNodes(
          patched,
          gpuNormal,
          normalScaleUniform.current,
        );
      }

      if (screenSpaceNormals) {
        (patched as unknown as NodeMaterial).normalNode = normalize(
          cross(dFdx(positionView), dFdy(positionView)),
        );
      } else if (blendedNormals && gpuNormal) {
        const flatN = normalize(cross(dFdx(positionView), dFdy(positionView)));
        const smoothN = normalize(
          transformNormalToView(attribute("normal", "vec3")),
        );
        (patched as unknown as NodeMaterial).normalNode = normalize(
          mix(smoothN, flatN, 0.5),
        );
      }

      patched.needsUpdate = true;
      return patched;
    },
    [textureSlots],
  );

  useEffect(() => {
    if (!skinnedMesh) return;

    if (needDebugPatch) {
      const baseMat =
        unpatchedMaterialRef.current ??
        (skinnedMesh.material as THREE.MeshStandardMaterial);
      if (patchedMaterialRef.current) patchedMaterialRef.current.dispose();

      unpatchedMaterialRef.current = baseMat;
      const patched = applyDebugPatch(baseMat);
      skinnedMesh.material = patched;
      patchedMaterialRef.current = patched;
    } else if (patchedMaterialRef.current) {
      if (unpatchedMaterialRef.current) {
        skinnedMesh.material = unpatchedMaterialRef.current;
      }
      patchedMaterialRef.current.dispose();
      patchedMaterialRef.current = null;
      unpatchedMaterialRef.current = null;
    }
  }, [skinnedMesh, needDebugPatch, applyDebugPatch]);

  useFrame(() => {
    if (!skinnedMesh || !needDebugPatch) return;
    if (skinnedMesh.material === patchedMaterialRef.current) return;

    const currentMat = skinnedMesh.material as THREE.MeshStandardMaterial;
    if (!currentMat.isMeshStandardMaterial) return;

    if (patchedMaterialRef.current) patchedMaterialRef.current.dispose();
    unpatchedMaterialRef.current = currentMat;
    const patched = applyDebugPatch(currentMat);
    skinnedMesh.material = patched;
    patchedMaterialRef.current = patched;
  });

  // ---------------------------------------------------------------------------
  // Per-frame: dispatch GPU normal compute
  // ---------------------------------------------------------------------------

  const diagFrameCounter = useRef(0);

  useFrame(({ gl }) => {
    if (!skinnedMesh || !gpuNormalRef.current) return;
    const gpuNormal = gpuNormalRef.current;

    // --- Sanity check: geometry vertex count must match GPU pipeline buffers ---
    // A mismatch means the geometry was swapped (e.g. subdivision) but the
    // pipeline hasn't been recreated yet. Dispatching would write to wrong
    // buffer offsets — skip entirely and wait for the pipeline effect to fire.
    const currentVertCount =
      skinnedMesh.geometry.getAttribute("position")?.count ?? 0;
    const pipelineVertCount = gpuNormal.normalSBA.count;
    if (currentVertCount !== pipelineVertCount) {
      shadingDiag.warn(
        "COMPUTE",
        "vert-count-mismatch",
        `Geometry verts (${currentVertCount}) != pipeline verts (${pipelineVertCount}) — skipping dispatch`,
        { currentVertCount, pipelineVertCount },
      );
      return;
    }

    // --- Dirty detection: only dispatch GPU compute when inputs changed ---
    let dirty = gpuDirty.current;
    gpuDirty.current = false;

    // Morph influences: compare-and-copy, returns true if any value differs.
    // In render mode, morphTargetInfluences is cleared on the mesh (to prevent
    // Three.js from creating morph textures for the morphless subdivided geo).
    // Read from the stashed array instead.
    const influenceSrc =
      stashedInfluencesRef.current ?? skinnedMesh.morphTargetInfluences;
    if (
      influenceSrc &&
      syncMorphInfluencesFrom(influenceSrc, gpuNormal.influenceData)
    ) {
      if (gpuNormal.influenceSBA) gpuNormal.influenceSBA.needsUpdate = true;
      dirty = true;
    }

    // Bone poses: reference check (Zustand replaces Map on mutation)
    if (bonePoses !== lastBonePosesRef.current) {
      dirty = true;
      lastBonePosesRef.current = bonePoses;
    }

    // Deformation mode flags — set TSL uniform values directly
    {
      const dqsTarget = shadingDebugFlags.dqsSkinning ? 1.0 : 0.0;
      if (gpuNormal.dqsBlendUniform.value !== dqsTarget) {
        gpuNormal.dqsBlendUniform.value = dqsTarget;
        dirty = true;
      }
    }

    // Periodic pre-dispatch validation (every 60 frames, regardless of dirty)
    if (++diagFrameCounter.current % 60 === 0) {
      validatePreDispatch(skinnedMesh, gpuNormal.influenceData);
    }

    const renderer = gl as unknown as THREE.WebGPURenderer;
    const queueSurfaceReadbackIfNeeded = () => {
      const sourceKey = [
        skinnedMesh.geometry.id,
        gpuPipelineGeneration,
        gpuSurfaceRevision.current,
      ].join(":");
      scheduleMannequinSurfaceReadback(renderer, sourceKey);
    };

    if (!dirty) {
      if (surfaceComputeInFlight.current) return;
      queueSurfaceReadbackIfNeeded();
      return;
    }

    // Ensure skeleton is up to date before compute reads bone matrices
    skinnedMesh.skeleton.update();

    // Dispatch all three compute passes with WebGPU validation error capture
    if (typeof renderer.compute === "function") {
      surfaceComputeInFlight.current = true;
      // Try to capture WebGPU validation errors around compute dispatch
      const device = renderer.backend?.device;
      if (device && diagFrameCounter.current <= 2) {
        device.pushErrorScope("validation");
        device.pushErrorScope("out-of-memory");
      }

      try {
        const computeSettles: Promise<unknown>[] = [];
        const dispatchNode = (node: any, label: string) => {
          const result = renderer.compute(node);
          if (result && typeof (result as any).catch === "function") {
            computeSettles.push(
              (result as Promise<unknown>).catch((err: unknown) => {
                shadingDiag.warn(
                  "COMPUTE",
                  `dispatch-fail-${label}`,
                  `Compute pass ${label} failed`,
                  { error: String(err) },
                );
              }),
            );
          }
        };

        // Pass 0: morph + sculpt + skin (LBS or LBS/DQS blend)
        dispatchNode(gpuNormal.computeNodes[0], "positions");

        // Corrective smooth: ping-pong Laplacian iterations on the delta
        if (shadingDebugFlags.correctiveSmooth) {
          if (gpuNormal.smoothPassA && gpuNormal.smoothPassB) {
            const iters = 5; // Fixed iteration count for trial mode
            for (let s = 0; s < iters; s++) {
              dispatchNode(gpuNormal.smoothPassA, `smooth-a-${s}`);
              dispatchNode(gpuNormal.smoothPassB, `smooth-b-${s}`);
            }
          }
        }

        // Pass 0.5: clear normal accumulators
        dispatchNode(gpuNormal.computeNodes[1], "clearAccum");
        // Pass 1: tri normals, Pass 2: normalize
        dispatchNode(gpuNormal.computeNodes[2], "triNormals");
        dispatchNode(gpuNormal.computeNodes[3], "normalize");
        const queueAfterCompute = () => {
          surfaceComputeInFlight.current = false;
          gpuSurfaceRevision.current++;
          queueSurfaceReadbackIfNeeded();
        };
        if (computeSettles.length > 0) {
          void Promise.allSettled(computeSettles).then(queueAfterCompute);
        } else {
          queueAfterCompute();
        }

        if (diagFrameCounter.current === 1) {
          shadingDiag.log(
            "COMPUTE",
            "dispatch-ok",
            `GPU compute dispatching (${gpuNormal.computeNodes.length} core passes)`,
          );
        }
      } catch (err) {
        surfaceComputeInFlight.current = false;
        shadingDiag.warn(
          "COMPUTE",
          "dispatch-error",
          `Compute dispatch threw: ${err}`,
          { error: String(err) },
        );
      }

      // Pop error scopes to get actual validation messages
      if (device && diagFrameCounter.current <= 2) {
        device.popErrorScope().then((err) => {
          if (err) {
            shadingDiag.warn(
              "COMPUTE",
              "gpu-oom",
              `GPU out-of-memory: ${err.message}`,
            );
          }
        });
        device.popErrorScope().then((err) => {
          if (err) {
            shadingDiag.warn(
              "COMPUTE",
              "gpu-validation",
              `GPU validation error: ${err.message}`,
              { message: err.message },
            );
          }
        });
      }
    } else {
      shadingDiag.warn(
        "COMPUTE",
        "no-compute",
        "renderer.compute not available — GPU normals will be stale",
      );
    }
  });

  // Disable Normal Map / Flat Shading — material-level overrides
  const origFlatShading = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    if (!skinnedMesh) return;
    const mat = skinnedMesh.material as THREE.MeshStandardMaterial;
    if (!mat) return;
    const active = debugEnabled && isPoseToolActive;

    // Normal map toggle — zeroes the scale uniform (for node materials) and
    // nulls mat.normalMap (for standard materials) to disable the normal map.
    // Applies regardless of debug/pose mode so it works from the toolbar toggle.
    if (shadingDebugFlags.disableNormalMap) {
      normalMapDisabled.current = true;
      normalScaleUniform.current.set(0, 0);
      if (mat.normalMap) {
        (mat as any)._savedNormalMap = mat.normalMap;
        mat.normalMap = null;
        mat.needsUpdate = true;
      }
    } else if (normalMapDisabled.current) {
      normalMapDisabled.current = false;
      const baseIntensity = resolvedMaterial?.normalIntensity ?? 1;
      normalScaleUniform.current.set(baseIntensity, baseIntensity);
      if ((mat as any)._savedNormalMap) {
        mat.normalMap = (mat as any)._savedNormalMap;
        delete (mat as any)._savedNormalMap;
        mat.needsUpdate = true;
      }
    }

    // Flat shading toggle
    if (active && shadingDebugFlags.flatShading) {
      if (origFlatShading.current === undefined) {
        origFlatShading.current = mat.flatShading;
      }
      mat.flatShading = true;
      mat.needsUpdate = true;
    } else if (origFlatShading.current !== undefined) {
      mat.flatShading = origFlatShading.current;
      mat.needsUpdate = true;
      origFlatShading.current = undefined;
    }
  }, [
    skinnedMesh,
    debugEnabled,
    isPoseToolActive,
    shadingDebugFlags.disableNormalMap,
    shadingDebugFlags.flatShading,
    resolvedMaterial,
  ]);

  // ---------------------------------------------------------------------------
  // Topology debug visualization
  // ---------------------------------------------------------------------------

  // Cached per-mesh data (computed once, invalidated on mesh change)
  const topoCache = useRef<{
    adjacency: Uint32Array[];
    restEdgeLengths: Float32Array;
    restPositions: Float32Array;
    restNormals: Float32Array;
    index: ArrayLike<number>;
    vertCount: number;
    // Scratch buffers (reused each frame)
    skinnedPos: Float32Array;
    skinnedNrm: Float32Array;
    colors: Float32Array;
    perVertex: Float32Array; // stretch ratios, dot products, or deviation angles
    // Static mode cache
    densityValues: Float32Array | null;
  } | null>(null);
  const topoColorAttr = useRef<THREE.BufferAttribute | null>(null);
  const topoMaterialApplied = useRef(false);

  // Build / invalidate the topology cache when the mesh changes
  useMemo(() => {
    topoCache.current = null;
    if (!skinnedMesh) return;
    const geo = skinnedMesh.geometry;
    const posAttr = geo.getAttribute("position");
    const nrmAttr = geo.getAttribute("normal");
    const idx = geo.index;
    if (!posAttr || !nrmAttr || !idx) return;

    const vertCount = posAttr.count;
    const restPositions = new Float32Array(posAttr.array as Float32Array);
    const restNormals = new Float32Array(nrmAttr.array as Float32Array);
    const indexArray = idx.array;
    const adjacency = buildAdjacency(indexArray, vertCount);
    const restEdgeLengths = computeRestEdgeLengths(restPositions, adjacency);

    topoCache.current = {
      adjacency,
      restEdgeLengths,
      restPositions,
      restNormals,
      index: indexArray,
      vertCount,
      skinnedPos: new Float32Array(vertCount * 3),
      skinnedNrm: new Float32Array(vertCount * 3),
      colors: new Float32Array(vertCount * 3),
      perVertex: new Float32Array(vertCount),
      densityValues: null,
    };
  }, [skinnedMesh]);

  // Per-frame topology visualization (stretch, normals, normalDeviation)
  useFrame(({ camera }) => {
    if (!debugEnabled || !activeTopologyViz || !skinnedMesh?.skeleton) return;
    const cache = topoCache.current;
    if (!cache) return;

    const geo = skinnedMesh.geometry;
    const skinIndexAttr = geo.getAttribute(
      "skinIndex",
    ) as THREE.BufferAttribute;
    const skinWeightAttr = geo.getAttribute(
      "skinWeight",
    ) as THREE.BufferAttribute;
    if (!skinIndexAttr || !skinWeightAttr) return;

    const siArr = skinIndexAttr.array;
    const swArr = skinWeightAttr.array;
    const siStride = skinIndexAttr.itemSize;
    const swStride = skinWeightAttr.itemSize;

    // Density is static — only compute once
    if (activeTopologyViz === "density") {
      if (!cache.densityValues) {
        cache.densityValues = computeDensity(
          cache.restPositions,
          cache.adjacency,
        );
      }
      for (let vi = 0; vi < cache.vertCount; vi++) {
        densityToColor(cache.densityValues[vi], cache.colors, vi * 3);
      }
    } else {
      // Per-frame modes need CPU skinning
      // Force skeleton to update bone matrices before we read them
      skinnedMesh.skeleton.update();

      const boneMatrices = skinnedMesh.skeleton
        .boneMatrices as Float32Array | null;
      const bindMatrix = skinnedMesh.bindMatrix;
      const bindMatrixInverse = skinnedMesh.bindMatrixInverse;
      if (!boneMatrices) return;

      if (activeTopologyViz === "stretch") {
        skinPositions(
          cache.restPositions,
          siArr,
          swArr,
          siStride,
          swStride,
          boneMatrices,
          bindMatrix,
          bindMatrixInverse,
          cache.skinnedPos,
        );
        const ratios = computeStretch(
          cache.skinnedPos,
          cache.adjacency,
          cache.restEdgeLengths,
        );
        for (let vi = 0; vi < cache.vertCount; vi++) {
          stretchToColor(ratios[vi], cache.colors, vi * 3);
        }
      } else if (activeTopologyViz === "normals") {
        skinPositions(
          cache.restPositions,
          siArr,
          swArr,
          siStride,
          swStride,
          boneMatrices,
          bindMatrix,
          bindMatrixInverse,
          cache.skinnedPos,
        );
        skinNormals(
          cache.restNormals,
          siArr,
          swArr,
          siStride,
          swStride,
          boneMatrices,
          bindMatrix,
          bindMatrixInverse,
          cache.skinnedNrm,
        );
        computeNormalOrientation(
          cache.skinnedPos,
          cache.skinnedNrm,
          camera.position,
          skinnedMesh.matrixWorld,
          cache.perVertex,
        );
        for (let vi = 0; vi < cache.vertCount; vi++) {
          orientationToColor(cache.perVertex[vi], cache.colors, vi * 3);
        }
      } else if (activeTopologyViz === "normalDeviation") {
        skinPositions(
          cache.restPositions,
          siArr,
          swArr,
          siStride,
          swStride,
          boneMatrices,
          bindMatrix,
          bindMatrixInverse,
          cache.skinnedPos,
        );
        skinNormals(
          cache.restNormals,
          siArr,
          swArr,
          siStride,
          swStride,
          boneMatrices,
          bindMatrix,
          bindMatrixInverse,
          cache.skinnedNrm,
        );
        computeNormalDeviation(
          cache.skinnedPos,
          cache.skinnedNrm,
          cache.index,
          cache.perVertex,
        );
        for (let vi = 0; vi < cache.vertCount; vi++) {
          deviationToColor(cache.perVertex[vi], cache.colors, vi * 3);
        }
      }
    }

    // Write vertex colors
    if (topoColorAttr.current) {
      (topoColorAttr.current.array as Float32Array).set(cache.colors);
      topoColorAttr.current.needsUpdate = true;
    } else {
      const attr = new THREE.BufferAttribute(cache.colors.slice(), 3);
      geo.setAttribute("color", attr);
      topoColorAttr.current = attr;
    }

    // Apply material if not already set
    if (!topoMaterialApplied.current) {
      skinnedMesh.material = TOPOLOGY_VIZ_MAT;
      topoMaterialApplied.current = true;
    }
  });

  // Clean up topology viz when disabled
  useEffect(() => {
    if (!skinnedMesh) return;
    if (debugEnabled && activeTopologyViz) return; // active — don't clean up

    if (topoColorAttr.current) {
      skinnedMesh.geometry.deleteAttribute("color");
      topoColorAttr.current = null;
    }
    topoMaterialApplied.current = false;
  }, [skinnedMesh, debugEnabled, activeTopologyViz]);

  // ---------------------------------------------------------------------------
  // Bone weight visualization
  // ---------------------------------------------------------------------------

  const weightColorAttr = useRef<THREE.BufferAttribute | null>(null);

  // Weight visualization: when a bone is selected in pose mode + debug, show vertex
  // colors mapping that bone's influence (red = 1.0, blue = 0.0)
  useEffect(() => {
    if (!skinnedMesh) return;
    const geo = skinnedMesh.geometry;

    // Topology viz takes priority over bone weight viz
    if (debugEnabled && activeTopologyViz) {
      if (weightColorAttr.current) {
        geo.deleteAttribute("color");
        weightColorAttr.current = null;
      }
      return;
    }

    if (
      debugEnabled &&
      shadingDebugFlags.boneInfluenceViz &&
      isPoseToolActive &&
      activeBoneName &&
      !debugMaterial &&
      skinnedMesh.skeleton
    ) {
      const boneObj = boneMap.get(activeBoneName);
      const boneIndex = boneObj
        ? skinnedMesh.skeleton.bones.indexOf(boneObj)
        : skinnedMesh.skeleton.bones.findIndex(
            (b) => b.name === activeBoneName,
          );

      if (boneIndex < 0) return;

      const skinIndex = geo.getAttribute("skinIndex") as THREE.BufferAttribute;
      const skinWeight = geo.getAttribute(
        "skinWeight",
      ) as THREE.BufferAttribute;
      if (!skinIndex || !skinWeight) return;

      const vertCount = geo.getAttribute("position").count;
      const colors = new Float32Array(vertCount * 3);
      const siArr = skinIndex.array;
      const swArr = skinWeight.array;
      const siStride = skinIndex.itemSize;
      const swStride = skinWeight.itemSize;

      for (let vi = 0; vi < vertCount; vi++) {
        let w = 0;
        for (let slot = 0; slot < 4; slot++) {
          if (Math.round(siArr[vi * siStride + slot]) === boneIndex) {
            w += swArr[vi * swStride + slot];
          }
        }
        weightToColor(w, colors, vi * 3);
      }

      const attr = new THREE.BufferAttribute(colors, 3);
      geo.setAttribute("color", attr);
      weightColorAttr.current = attr;

      skinnedMesh.material = BONE_WEIGHT_VIZ_MAT;
      return;
    }

    // Remove weight colors when not visualizing
    if (weightColorAttr.current) {
      geo.deleteAttribute("color");
      weightColorAttr.current = null;
    }
  }, [
    skinnedMesh,
    isPoseToolActive,
    activeBoneName,
    boneMap,
    debugEnabled,
    debugMaterial,
    activeTopologyViz,
    shadingDebugFlags.boneInfluenceViz,
  ]);

  // ---------------------------------------------------------------------------
  // Shading diagnostic viz modes — vertex-color-based (like topology viz)
  // ---------------------------------------------------------------------------

  const diagVizColorAttr = useRef<THREE.BufferAttribute | null>(null);
  const diagVizActive =
    debugEnabled &&
    (shadingDebugFlags.gpuNormalBufferViz ||
      shadingDebugFlags.morphInfluenceHeatmap ||
      shadingDebugFlags.uvIslandViz);

  useEffect(() => {
    if (!skinnedMesh) return;
    const geo = skinnedMesh.geometry;

    if (!diagVizActive) {
      // Clean up when deactivated
      if (diagVizColorAttr.current) {
        geo.deleteAttribute("color");
        diagVizColorAttr.current = null;
      }
      return;
    }

    const vertCount = geo.getAttribute("position").count;
    const colors = new Float32Array(vertCount * 3);

    if (shadingDebugFlags.gpuNormalBufferViz && gpuNormalRef.current) {
      // The normalSBA.array is stale (CPU-side, never updated from GPU).
      // Use the geometry's "normal" attribute which IS the normalSBA — but
      // we need the actual GPU data. Trigger an async readback.
      const gpuNormal = gpuNormalRef.current;
      const gl = window.__poseref_renderer;
      if (gl && typeof gl.getArrayBufferAsync === "function") {
        gl.getArrayBufferAsync(gpuNormal.normalSBA).then((buf: ArrayBuffer) => {
          const nrmSrc = new Float32Array(buf);
          const stride = nrmSrc.length / vertCount;
          const freshColors = new Float32Array(vertCount * 3);
          for (let i = 0; i < vertCount; i++) {
            freshColors[i * 3] = nrmSrc[i * stride] * 0.5 + 0.5;
            freshColors[i * 3 + 1] = nrmSrc[i * stride + 1] * 0.5 + 0.5;
            freshColors[i * 3 + 2] = nrmSrc[i * stride + 2] * 0.5 + 0.5;
          }
          const attr = geo.getAttribute("color") as
            | THREE.BufferAttribute
            | undefined;
          if (attr && attr.array.length === freshColors.length) {
            (attr.array as Float32Array).set(freshColors);
            attr.needsUpdate = true;
          }
        });
      }
      // Set initial colors from stale CPU data as placeholder
      const nrmArr = gpuNormal.normalSBA.array as Float32Array;
      const nrmStride = nrmArr.length / vertCount;
      for (let i = 0; i < vertCount; i++) {
        colors[i * 3] = nrmArr[i * nrmStride] * 0.5 + 0.5;
        colors[i * 3 + 1] = nrmArr[i * nrmStride + 1] * 0.5 + 0.5;
        colors[i * 3 + 2] = nrmArr[i * nrmStride + 2] * 0.5 + 0.5;
      }
    } else if (shadingDebugFlags.morphInfluenceHeatmap) {
      // Compute per-vertex max weighted displacement magnitude
      const morphPositions = geo.morphAttributes?.position as
        | THREE.BufferAttribute[]
        | undefined;
      const influences = skinnedMesh.morphTargetInfluences;
      if (morphPositions && influences) {
        for (let vi = 0; vi < vertCount; vi++) {
          let maxDisp = 0;
          for (let mi = 0; mi < morphPositions.length; mi++) {
            const w = Math.abs(influences[mi] ?? 0);
            if (w < 1e-6) continue;
            const arr = morphPositions[mi].array as Float32Array;
            const dx = arr[vi * 3];
            const dy = arr[vi * 3 + 1];
            const dz = arr[vi * 3 + 2];
            const disp = w * Math.sqrt(dx * dx + dy * dy + dz * dz);
            if (disp > maxDisp) maxDisp = disp;
          }
          // Normalize: typical morph deltas are 0-0.1 range for subtle, 0-0.5 for strong
          const normalized = Math.min(1, maxDisp * 10);
          weightToColor(normalized, colors, vi * 3);
        }
      }
    } else if (shadingDebugFlags.uvIslandViz) {
      // Flood-fill UV islands via triangle connectivity, only connecting
      // vertices that share the same UV coordinates. Each island gets a
      // distinct pseudorandom color so seam boundaries are immediately visible.
      const uvAttr = geo.getAttribute("uv") as
        | THREE.BufferAttribute
        | undefined;
      const idxAttr = geo.index;
      if (uvAttr && idxAttr) {
        const indices = idxAttr.array;
        const triCount = indices.length / 3;

        // Build per-vertex adjacency in UV space: two vertices are connected
        // only if they share a triangle edge AND have the same UV at their
        // respective positions. This means seam-split vertices (same position,
        // different UV) are NOT connected — they land in separate islands.
        const adj = new Array<Set<number>>(vertCount);
        for (let i = 0; i < vertCount; i++) adj[i] = new Set();

        for (let ti = 0; ti < triCount; ti++) {
          const i0 = indices[ti * 3];
          const i1 = indices[ti * 3 + 1];
          const i2 = indices[ti * 3 + 2];
          // All three edges of this triangle connect in UV space
          adj[i0].add(i1);
          adj[i1].add(i0);
          adj[i0].add(i2);
          adj[i2].add(i0);
          adj[i1].add(i2);
          adj[i2].add(i1);
        }

        // Flood-fill to find connected components (UV islands)
        const islandId = new Int32Array(vertCount).fill(-1);
        let nextIsland = 0;

        for (let seed = 0; seed < vertCount; seed++) {
          if (islandId[seed] >= 0) continue;
          const id = nextIsland++;
          const queue = [seed];
          islandId[seed] = id;
          while (queue.length > 0) {
            const v = queue.pop()!;
            for (const n of adj[v]) {
              if (islandId[n] < 0) {
                islandId[n] = id;
                queue.push(n);
              }
            }
          }
        }

        // Assign a distinct color per island using a seeded hash for
        // high-contrast, deterministic colors.
        for (let vi = 0; vi < vertCount; vi++) {
          const id = islandId[vi];
          // Simple hash → HSL with high saturation for visual contrast
          const h = ((id * 2654435761) >>> 0) % 360;
          const s = 0.75;
          const l = 0.45 + ((id * 7919) % 20) / 100; // 0.45–0.64

          // HSL to RGB (inline for zero-dependency)
          const c = (1 - Math.abs(2 * l - 1)) * s;
          const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
          const m = l - c / 2;
          let r = 0,
            g = 0,
            b = 0;
          if (h < 60) {
            r = c;
            g = x;
          } else if (h < 120) {
            r = x;
            g = c;
          } else if (h < 180) {
            g = c;
            b = x;
          } else if (h < 240) {
            g = x;
            b = c;
          } else if (h < 300) {
            r = x;
            b = c;
          } else {
            r = c;
            b = x;
          }
          colors[vi * 3] = r + m;
          colors[vi * 3 + 1] = g + m;
          colors[vi * 3 + 2] = b + m;
        }

        shadingDiag.log(
          "BUFFER",
          "uv-island-viz",
          `UV island visualization: ${nextIsland} islands across ${vertCount} vertices`,
        );
      }
    }

    const attr = new THREE.BufferAttribute(colors, 3);
    geo.setAttribute("color", attr);
    diagVizColorAttr.current = attr;

    skinnedMesh.material = DIAG_VIZ_MAT;
  }, [
    skinnedMesh,
    diagVizActive,
    shadingDebugFlags.gpuNormalBufferViz,
    shadingDebugFlags.morphInfluenceHeatmap,
    shadingDebugFlags.uvIslandViz,
  ]);

  // Apply material based on view mode (skipped when debug weight/topology viz is active,
  // unless a debug material override is explicitly set)
  useEffect(() => {
    if (!clonedScene) return;
    if (debugEnabled && activeTopologyViz && !debugMaterial) return;
    if (debugEnabled && isPoseToolActive && activeBoneName && !debugMaterial)
      return;
    if (diagVizActive) return;

    // In render mode the GPU pipeline is required for correct normals.
    // Skip if the pipeline hasn't been created yet — the pipeline effect
    // will trigger this effect again via gpuPipelineGeneration once ready.
    // Applying a plain material now would overwrite the correct patched one
    // when cascading dep changes cause multiple material effect fires.
    if (isRenderMode && !gpuNormalRef.current) return;

    const mat = resolvedMaterial;
    const flat = (shadingOverride ?? obj.shading) === "flat";
    const matOptions = {
      viewMode,
      matcapPreset,
      objectXray: objectXrayActive,
      wireframeXray,
      showTextures,
      textureMapsEnabled,
    };
    const matConfig = sceneMaterialToConfig(mat, flat, textureSlots);

    // Allocate the body source material. Owned by this effect and tracked
    // in `ownedMannequinMaterialsRef` for disposal on replay/unmount.
    const bodyBase = createViewportMaterial(matConfig, matOptions);

    shadingDiag.log(
      "MATERIAL",
      "apply-init",
      `Creating material — viewMode=${viewMode}, gpuPipeline=${!!gpuNormalRef.current}`,
      {
        geoId: skinnedMesh?.geometry.id,
        geoVertCount:
          skinnedMesh?.geometry.getAttribute("position")?.count ?? 0,
        gpuPipelineGen: gpuPipelineGeneration,
      },
    );

    // Body: when the GPU normal compute pipeline is active, wrap the base
    // in a fresh NodeMaterial (so normalNode can reference this mannequin's
    // compute SBA). `toNodeMaterial` returns a brand-new instance; the
    // source `bodyBase` is no longer needed and is disposed immediately.
    // Otherwise the body IS the base and we hand ownership over to the
    // owned-ref slot.
    let body: THREE.Material;
    if (gpuNormalRef.current) {
      // Set initial normal scale before patching
      const baseIntensity = resolvedMaterial?.normalIntensity ?? 1;
      normalScaleUniform.current.set(baseIntensity, baseIntensity);

      body = toNodeMaterial(bodyBase);
      applyMannequinNormalNodes(
        body,
        gpuNormalRef.current,
        normalScaleUniform.current,
      );
      body.needsUpdate = true;
      // The node material copied bodyBase's state into an independent GPU
      // pipeline; source is no longer referenced. Material.dispose() does
      // not touch referenced textures, so this is safe for shared slots.
      bodyBase.dispose();
    } else {
      body = bodyBase;
    }
    body.shadowSide = THREE.BackSide;

    // Validate material patch
    if (skinnedMesh) {
      validateMaterialPatch(body, skinnedMesh.geometry, !!gpuNormalRef.current);
    }

    let meshCount = 0;
    let skippedOverlays = 0;
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        // Skip overlay child meshes — they have their own material
        // management (wireframe, depth prepass, selection outline, sketch).
        // Overwriting their materials here would break their rendering.
        const ud = child as any;
        if (
          ud._wireOverlay ||
          ud._depthPrepass ||
          ud._selectionOutlinePrepass ||
          ud._hiddenSelectionGhost ||
          child.userData.sketchOverlay
        ) {
          skippedOverlays++;
          return;
        }
        child.material = body;
        child.castShadow = shadowsEnabled;
        child.receiveShadow = shadowsEnabled;
        meshCount++;
      }
    });
    shadingDiag.log(
      "MATERIAL",
      "apply-done",
      `Applied material to ${meshCount} mesh(es), skipped ${skippedOverlays} overlay(s)`,
    );

    // Now safe to dispose the stale pipeline — the old material's normalNode
    // references have been replaced by the new material on every mesh.
    if (staleGpuPipelineRef.current) {
      staleGpuPipelineRef.current.dispose();
      staleGpuPipelineRef.current = null;
    }

    // Reset the debug-patch refs before disposing the previous body. The
    // shading-debug effect captures `skinnedMesh.material` into
    // `unpatchedMaterialRef` when it patches; if we dispose that captured
    // reference without clearing the ref, the next debug-effect run would
    // restore a dangling pointer. Clearing here causes the debug effect to
    // re-patch over the new body on the next tick.
    if (patchedMaterialRef.current) {
      patchedMaterialRef.current.dispose();
      patchedMaterialRef.current = null;
      unpatchedMaterialRef.current = null;
    }

    // Dispose the previously-owned body if it differs from the new one,
    // then install the new ownership slot.
    const prevOwned = ownedMannequinMaterialsRef.current;
    ownedMannequinMaterialsRef.current = { body };
    if (prevOwned && prevOwned.body !== body) prevOwned.body.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clonedScene,
    viewMode,
    matcapPreset,
    objectXrayActive,
    wireframeXray,
    showTextures,
    textureMapsEnabled,
    textureSlots,
    resolvedMaterial,
    obj.shading,
    shadingOverride,
    shadowsEnabled,
    debugEnabled,
    debugMaterial,
    activeTopologyViz,
    isPoseToolActive,
    activeBoneName,
    gpuPipelineGeneration,
    diagVizActive,
    isRenderMode,
    skinnedMesh,
  ]);

  // Wireframe overlay — shown when wireframe toggle is on (same as primitives)
  const showWireframeOverlay = showWireframe && viewMode !== "wireframe";
  const activeSelectedObjectId = useStore(getActiveSelectedObjectIdFromState);
  const isSculptDimmed = useStore(
    (s) => s.sculptMode !== null && s.sculptMode.objectId !== obj.id,
  );
  const eyedropperHovered = useStore(
    (s) => s.eyedropperSession !== null && s.eyedropperHoveredId === obj.id,
  );
  const showSelectionWire =
    (selected || eyedropperHovered) &&
    effectiveVisible &&
    !isPoseToolActive &&
    showSceneInteraction;
  const needsDepthPrepass = viewMode === "wireframe" && !wireframeXray;
  const selectionPrepassMaterial = useMemo(() => {
    const base = getSelectionPrepassMaterial(
      obj.id,
      "default",
      eyedropperHovered || obj.id === activeSelectedObjectId,
    );
    const gpuNormal = gpuNormalRef.current;
    if (!gpuNormal) return base;

    const patched = toNodeMaterial(base);
    patchPositionForGpuSkinning(patched, gpuNormal);
    patched.needsUpdate = true;
    return patched;
  }, [obj.id, eyedropperHovered, activeSelectedObjectId]);
  useEffect(() => {
    return () => {
      if (selectionPrepassMaterial.isNodeMaterial) {
        selectionPrepassMaterial.dispose();
      }
    };
  }, [selectionPrepassMaterial]);

  useEffect(() => {
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        markInkingCategory(child, "mannequin");
      }
    });
  }, [clonedScene]);

  useEffect(() => {
    if (!skinnedMesh) return;
    skinnedMesh.visible = effectiveVisible;
    return () => {
      skinnedMesh.visible = true;
    };
  }, [skinnedMesh, effectiveVisible]);

  // Wireframe overlay material — shared module singleton, picked by xray flag.
  const wireOverlayMat = wireframeXray
    ? WIRE_OVERLAY_XRAY_MAT
    : WIRE_OVERLAY_MAT;

  // Depth pre-pass, wireframe overlay, selection outline — all use the same
  // skinned-mesh-child pattern, extracted into useSkinnedOverlay.
  useSkinnedOverlay(
    skinnedMesh,
    needsDepthPrepass,
    WIREFRAME_DEPTH_PREPASS_MAT,
    -1,
    "depthPrepass",
    [geoGeneration],
  );
  useSkinnedOverlay(
    skinnedMesh,
    showWireframeOverlay,
    wireOverlayMat,
    1,
    "wireOverlay",
    [geoGeneration],
  );
  useSkinnedOverlay(
    skinnedMesh,
    showSelectionWire,
    selectionPrepassMaterial,
    0,
    "selectionOutlinePrepass",
    [geoGeneration, activeSelectedObjectId],
    { layer: SELECTION_OUTLINE_LAYER },
  );
  useSkinnedOverlay(
    skinnedMesh,
    showHiddenSelectionGhost,
    HIDDEN_SELECTION_GHOST_MAT,
    HIDDEN_SELECTION_GHOST_RENDER_ORDER,
    "hiddenSelectionGhost",
    [geoGeneration],
    { attach: "sibling" },
  );
  // Sculpt dim — darkens this mannequin when another object is being sculpted
  useSkinnedOverlay(
    skinnedMesh,
    isSculptDimmed,
    SCULPT_DIM_MAT,
    3,
    "sculptDim",
    [geoGeneration],
  );

  // In pose mode, disable raycasting on the mesh so clicks reach joint spheres
  useEffect(() => {
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.raycast =
          isPoseToolActive || !effectiveVisible ? () => {} : acceleratedRaycast;
      }
    });
  }, [clonedScene, isPoseToolActive, effectiveVisible]);

  // Tap → select; double-tap → enter pose mode (only in object mode)
  const isObjectMode = useStore((s) => s.workspaceMode === "object");
  const doubleTapPose = useCallback(
    () => enterPoseMode(obj.id),
    [enterPoseMode, obj.id],
  );
  const {
    handlePointerDown,
    handlePointerUp,
    handlePointerEnter,
    handlePointerLeave,
  } = useObjectTap(obj.id, isObjectMode ? doubleTapPose : undefined);

  return (
    <>
      <group
        ref={mannequinGroupRef}
        name={obj.id}
        userData={{
          poserefObjectId: obj.id,
          poserefInkingInclude: "mannequin",
        }}
        visible={renderVisibleInViewport}
        position={worldTransform.position}
        rotation={worldTransform.rotation}
        scale={worldTransform.scale}
        onPointerDown={
          !isPoseToolActive && effectiveVisible ? handlePointerDown : undefined
        }
        onPointerUp={
          !isPoseToolActive && effectiveVisible ? handlePointerUp : undefined
        }
        onPointerEnter={effectiveVisible ? handlePointerEnter : undefined}
        onPointerLeave={effectiveVisible ? handlePointerLeave : undefined}
      >
        <group position={obj.originOffset ?? ZERO_VEC}>
          <primitive object={clonedScene} />
        </group>
      </group>

      {isPoseToolActive && allBones.length > 0 && (
        <BoneOverlay
          allBones={allBones}
          majorBones={majorBones}
          objectId={obj.id}
          debugEnabled={debugEnabled}
        />
      )}

      {debugEnabled && isPoseToolActive && (
        <JointAngleDataPump
          boneMap={boneMap}
          restPoses={getRestPoses(obj.id) ?? new Map()}
          bonePoses={bonePoses}
          correctiveBindings={correctiveBindings}
        />
      )}
    </>
  );
}

// BoneOverlay and BoneDebugOverlay are shared components in BoneOverlay.tsx

// MannequinRenderer top-level function removed — EntityRenderer now handles
// iteration and composition. MannequinNode is the public API from this file.
